// Storage Permissions - Permission helper functions for multi-tenancy
// Split from storage.ts for maintainability
// NOTE: Use function declarations (not const arrows) to avoid TDZ errors
// when re-exported via 'export *' in storage.ts barrel file.

import type { UserPermissions, TaxiCentral, Owner } from './storage-types';

/**
 * Filter user permissions based on central's available features
 */
export function filterPermissionsByCentralFeatures(
  permissions: UserPermissions,
  centralFeatures?: TaxiCentral['availableFeatures']
): UserPermissions {
  if (!centralFeatures) return permissions;

  return {
    tabs: {
      reports: permissions.tabs.reports && (centralFeatures.reports ?? true),
      settings: permissions.tabs.settings && (centralFeatures.settings ?? true),
      carInfo: permissions.tabs.carInfo && (centralFeatures.carInfo ?? true),
      trips: permissions.tabs.trips && (centralFeatures.trips ?? true),
      accounting: permissions.tabs.accounting && (centralFeatures.accounting ?? false),
    },
    subtabs: {
      salary: permissions.subtabs.salary && (centralFeatures.salary ?? true),
      shifts: permissions.subtabs.shifts && (centralFeatures.shifts ?? true),
      terminalReports: permissions.subtabs.terminalReports && (centralFeatures.terminalReports ?? true),
      travelExpenses: permissions.subtabs.travelExpenses && (centralFeatures.travelExpenses ?? true),
      drivers: permissions.subtabs.drivers,
      bankAccounts: permissions.subtabs.bankAccounts,
      templates: permissions.subtabs.templates,
      companyInfo: permissions.subtabs.companyInfo,
      apiConfig: permissions.subtabs.apiConfig,
      userManagement: permissions.subtabs.userManagement,
      debugLogs: permissions.subtabs.debugLogs,
      bankChecker: permissions.subtabs.bankChecker && (centralFeatures.bankChecker ?? false),
      reportUpload: permissions.subtabs.reportUpload && (centralFeatures.reportUpload ?? false),
      accountingSettings: permissions.subtabs.accountingSettings && (centralFeatures.accountingSettings ?? false),
    },
    actions: permissions.actions,
    notifications: permissions.notifications || { supportTickets: true, reportGenerated: true, systemAlerts: true, editChanges: false },
    compliance: permissions.compliance,
  };
}

/**
 * Check if user is a super admin (master admin who can manage all centrals)
 */
export function isSuperAdmin(user: Owner | null): boolean {
  return user?.isSuperAdmin === true;
}

/**
 * Check if user is a central admin (can manage users in their central only)
 */
export function isCentralAdmin(user: Owner | null): boolean {
  return user?.isCentralAdmin === true;
}

/**
 * Check if user has any admin privileges (super or central)
 */
export function isAnyAdmin(user: Owner | null): boolean {
  return isSuperAdmin(user) || isCentralAdmin(user);
}

/**
 * Check if user can manage another user (for central admins)
 */
export function canManageUser(admin: Owner | null, targetUser: Owner): boolean {
  if (!admin) return false;
  if (isSuperAdmin(admin)) return true;
  if (isCentralAdmin(admin) && admin.centralCode === targetUser.centralCode) return true;
  return false;
}

/**
 * Check if user can access a central's settings
 */
export function canAccessCentral(user: Owner | null, centralCode: string): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  if (isCentralAdmin(user) && user.centralCode === centralCode) return true;
  return false;
}

// ============================================================================
// Admin View Mode - allows admins to toggle between admin and owner view
// ============================================================================

const ADMIN_VIEW_MODE_KEY = 'voss_taxi_admin_view_mode';

/**
 * Get the current admin view mode.
 * 'admin' = see all data (default), 'owner' = see only own løyver
 */
export function getAdminViewMode(): 'admin' | 'owner' {
  if (typeof window === 'undefined') return 'admin';
  return (localStorage.getItem(ADMIN_VIEW_MODE_KEY) as 'admin' | 'owner') || 'admin';
}

/**
 * Set admin view mode
 */
export function setAdminViewMode(mode: 'admin' | 'owner'): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(ADMIN_VIEW_MODE_KEY, mode);
  // Dispatch event for cross-component sync
  window.dispatchEvent(new CustomEvent('admin-view-mode-changed', { detail: mode }));
}

/**
 * Check if admin is currently viewing as admin (not in owner mode).
 * Returns true for non-admins (they always see their own data).
 * For admins, returns true only when in admin view mode.
 */
export function isAdminViewActive(user: Owner | null): boolean {
  return isAnyAdmin(user) && getAdminViewMode() === 'admin';
}
